import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Button,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import axios from 'axios';
import { useUserStore } from 'store';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
  DateRangeFilter,
} from '../shared/ListFilters';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import DataTable from '../shared/DataTable';
import TrialFeatureNotice from '../shared/TrialFeatureNotice';
import BookingActions from './BookingActions';
import BookingDetailDialog from './BookingDetailDialog';

const STORAGE_KEY = 'booking_list_preferences';

const STATUSES = ['pending', 'confirmed', 'completed', 'cancelled_by_customer', 'cancelled_by_workshop', 'no_show'];

const BOOKING_STATUS_CONFIG = {
  pending: { color: '#f59e0b', bg: '#fef3c7', labelKey: 'booking.status.pending' },
  confirmed: { color: '#3b82f6', bg: '#dbeafe', labelKey: 'booking.status.confirmed' },
  completed: { color: '#10b981', bg: '#d1fae5', labelKey: 'booking.status.completed' },
  cancelled_by_customer: { color: '#ef4444', bg: '#fee2e2', labelKey: 'booking.status.cancelled_by_customer' },
  cancelled_by_workshop: { color: '#6b7280', bg: '#f3f4f6', labelKey: 'booking.status.cancelled_by_workshop' },
  no_show: { color: '#8b5cf6', bg: '#ede9fe', labelKey: 'booking.status.no_show' },
};

const CANCELLED_STATUSES = ['cancelled_by_customer', 'cancelled_by_workshop', 'no_show'];

const defaultPreferences = {
  sortField: 'booking_date',
  sortDirection: 'desc',
  filters: {
    status: '',
    hide_cancelled: true,
    dateRangeType: 'all',
    date_from: '',
    date_to: '',
  },
  searchQuery: '',
};

function BookingStatusBadge({ status, t }) {
  const config = BOOKING_STATUS_CONFIG[status] || BOOKING_STATUS_CONFIG.pending;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: config.bg,
        color: config.color,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: config.color,
        }}
      />
      {t(config.labelKey)}
    </Box>
  );
}

function ReminderIcon({ booking, t }) {
  if (booking.reminder_sent) {
    return (
      <Tooltip title={t('booking.reminder.sent')}>
        <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#10b981' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={t('booking.reminder.not_sent')}>
      <RemoveCircleOutlineIcon fontSize="small" sx={{ color: '#9ca3af' }} />
    </Tooltip>
  );
}

function BookingsPage({ t }) {
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({ pending: 0, today: 0, week: 0 });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from the dashboard bookings widget: /bookings?booking=<id> opens the detail dialog.
  useEffect(() => {
    const bookingId = searchParams.get('booking');
    if (!bookingId) return;
    let cancelled = false;
    axios
      .get(`/api/bookings/${bookingId}/`)
      .then((res) => {
        if (!cancelled) setSelectedBooking(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete('booking');
        setSearchParams(next, { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('booking')]);
  const [customFormFields, setCustomFormFields] = useState([]);

  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    handleSort: handleLocalSort,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Fetch custom form fields for the detail dialog
  useEffect(() => {
    axios.get('/api/booking-config/').then(res => {
      setCustomFormFields(res.data.custom_form_fields || []);
    }).catch(() => {});
  }, []);

  // Build server-side filter params
  const serverFilters = useMemo(() => {
    const filters = {};
    if (preferences.filters.status) {
      filters.status = preferences.filters.status;
    } else if (preferences.filters.hide_cancelled !== false) {
      // Default: exclude cancelled/no_show
      filters.exclude_status = CANCELLED_STATUSES.join(',');
    }
    if (preferences.filters.date_from) {
      filters.date_from = preferences.filters.date_from;
    }
    if (preferences.filters.date_to) {
      filters.date_to = preferences.filters.date_to;
    }
    return filters;
  }, [preferences.filters]);

  const {
    data: items,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    setFilters: setServerFilters,
    setSort,
    refetch,
  } = usePaginatedList('/api/bookings/', {
    initialFilters: serverFilters,
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync local preferences to server filters
  useEffect(() => {
    setServerFilters(serverFilters);
  }, [serverFilters, setServerFilters]);

  useEffect(() => {
    setSort(preferences.sortField, preferences.sortDirection);
  }, [preferences.sortField, preferences.sortDirection, setSort]);

  useEffect(() => {
    setSearchQuery(preferences.searchQuery);
  }, [preferences.searchQuery, setSearchQuery]);

  // Fetch booking stats independently of the paginated list. The stats are not
  // derived from `items`, so keying this effect on `items` caused all three stat
  // requests (incl. /api/bookings/pending_count/) to refire on every list
  // refetch. Refresh only on mount and via an explicit signal after actions.
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

        const [pendingRes, todayRes, weekRes] = await Promise.all([
          axios.get('/api/bookings/pending_count/'),
          axios.get('/api/bookings/', { params: { date_from: today, date_to: today, page_size: 1 } }),
          axios.get('/api/bookings/', { params: { date_from: today, date_to: weekEnd, page_size: 1 } }),
        ]);

        setStats({
          pending: pendingRes.data.count || 0,
          today: todayRes.data.count || 0,
          week: weekRes.data.count || 0,
        });
      } catch (err) {
        console.error('Failed to fetch booking stats:', err);
      }
    };
    fetchStats();
  }, [statsRefreshKey]);

  const handleSort = useCallback((field) => {
    handleLocalSort(field);
  }, [handleLocalSort]);

  const handleActionComplete = useCallback((message, severity) => {
    setSnackbar({ open: true, message, severity });
    refetch();
    setStatsRefreshKey((k) => k + 1);
  }, [refetch]);

  const handleRowClick = useCallback((booking) => {
    setSelectedBooking(booking);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fi-FI');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  // Count active filters
  const activeFilterCount = [
    preferences.filters.status,
    preferences.filters.date_from,
    preferences.filters.date_to,
    preferences.filters.hide_cancelled === false, // non-default = showing cancelled
  ].filter(Boolean).length;

  // Filter summary items
  const filterSummaryItems = [];
  if (preferences.filters.status) {
    filterSummaryItems.push({
      key: 'status',
      label: t(BOOKING_STATUS_CONFIG[preferences.filters.status]?.labelKey || preferences.filters.status),
      onRemove: () => updateLocalFilters({ status: '' }),
    });
  }
  if (preferences.filters.date_from || preferences.filters.date_to) {
    const label = [preferences.filters.date_from, preferences.filters.date_to].filter(Boolean).join(' – ');
    filterSummaryItems.push({
      key: 'date',
      label,
      onRemove: () => updateLocalFilters({ date_from: '', date_to: '' }),
    });
  }

  return (
    <Box sx={{ py: { xs: 0, md: 3 }, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3, px: { xs: 2, md: 0 } }}>
        <TrialFeatureNotice />
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          {t('booking.list.title')}
        </Typography>

        {/* Stats cards */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 100 }}>
            <Typography variant="body2" color="text.secondary">{t('booking.list.pending_count')}</Typography>
            <Typography variant="h5" fontWeight={700} color={stats.pending > 0 ? 'warning.main' : 'text.primary'}>
              {stats.pending}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 100 }}>
            <Typography variant="body2" color="text.secondary">{t('booking.list.today_count')}</Typography>
            <Typography variant="h5" fontWeight={700}>{stats.today}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 100 }}>
            <Typography variant="body2" color="text.secondary">{t('booking.list.week_count')}</Typography>
            <Typography variant="h5" fontWeight={700}>{stats.week}</Typography>
          </Paper>
        </Box>
      </Box>

      {/* Filter bar */}
      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <SearchInput
            value={preferences.searchQuery}
            onChange={(val) => updatePreferences({ searchQuery: val })}
            onClear={() => updatePreferences({ searchQuery: '' })}
            placeholder={t('Search') + '...'}
          />
          <FilterButton
            t={t}
            activeCount={activeFilterCount}
            isOpen={filtersOpen}
            onClick={toggleFiltersOpen}
          />
        </Box>

        <FilterPanel open={filtersOpen} t={t} resultCount={totalCount} onReset={resetLocalFilters} resetDisabled={activeFilterCount === 0}>
          <FilterSection title={t('booking.list.status')}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {STATUSES.map((s) => (
                <FilterChip
                  key={s}
                  label={t(BOOKING_STATUS_CONFIG[s]?.labelKey || s)}
                  active={preferences.filters.status === s}
                  onClick={() => {
                    const newStatus = preferences.filters.status === s ? '' : s;
                    updateLocalFilters({ status: newStatus, hide_cancelled: newStatus ? true : preferences.filters.hide_cancelled });
                  }}
                />
              ))}
            </Box>
            <Box sx={{ mt: 1 }}>
              <FilterChip
                label={t('booking.list.hide_cancelled')}
                active={preferences.filters.hide_cancelled !== false}
                onClick={() => updateLocalFilters({ hide_cancelled: preferences.filters.hide_cancelled === false ? true : false, status: '' })}
              />
            </Box>
          </FilterSection>
          <FilterSection title={t('booking.list.date_time')}>
            <DateRangeFilter
              t={t}
              dateRangeType={preferences.filters.dateRangeType || 'all'}
              dateFrom={preferences.filters.date_from}
              dateTo={preferences.filters.date_to}
              onTypeChange={(val) => updateLocalFilters({ dateRangeType: val })}
              onFromChange={(val) => updateLocalFilters({ date_from: val })}
              onToChange={(val) => updateLocalFilters({ date_to: val })}
            />
          </FilterSection>
        </FilterPanel>

        {filterSummaryItems.length > 0 && (
          <FilterSummary chips={filterSummaryItems} onClear={resetLocalFilters} t={t} />
        )}
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && items.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('booking.list.no_bookings')}
          </Typography>
        </Paper>
      )}

      {/* Desktop + Mobile list */}
      {items.length > 0 && (
        <>
          <DataTable
            columns={[
              {
                key: 'date',
                label: t('booking.list.date_time'),
                sortable: true,
                sortField: 'booking_date',
                width: '1%',
                noWrap: true,
                render: (booking) => (
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(booking.booking_date)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.booking_mode === 'drop_off'
                        ? (booking.drop_off_window_name || t('booking.list.drop_off'))
                        : formatTime(booking.booking_time)
                      }
                    </Typography>
                  </Box>
                ),
              },
              {
                key: 'service',
                label: t('booking.list.service'),
                maxWidth: 250,
                render: (booking) => (
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {booking.service_name || '–'}
                  </Typography>
                ),
              },
              {
                key: 'customer',
                label: t('booking.list.customer'),
                render: (booking) => (
                  <Box>
                    <Typography variant="body2">
                      {booking.customer_name}
                      {booking.is_new_customer && (
                        <Chip
                          label={t('booking.list.new_customer')}
                          size="small"
                          color="info"
                          sx={{ ml: 1, height: 20, fontSize: '10px' }}
                        />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.customer_phone}
                    </Typography>
                  </Box>
                ),
              },
              {
                key: 'vehicle',
                label: t('booking.list.vehicle'),
                width: '1%',
                noWrap: true,
                render: (booking) => (
                  <Typography variant="body2" fontWeight={500}>
                    {booking.vehicle_registration}
                  </Typography>
                ),
              },
              {
                key: 'status',
                label: t('booking.list.status'),
                width: '1%',
                render: (booking) => <BookingStatusBadge status={booking.status} t={t} />,
              },
              {
                key: 'reminder',
                label: '',
                width: '1%',
                align: 'center',
                hideBelow: 'lg',
                render: (booking) => <ReminderIcon booking={booking} t={t} />,
              },
              {
                key: 'actions',
                label: t('booking.list.actions'),
                width: '1%',
                noWrap: true,
                render: (booking) => (
                  <Box onClick={(e) => e.stopPropagation()}>
                    {booking.created_work_order && !['pending'].includes(booking.status) ? (
                      <Button
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => navigate(`/maintenances/${booking.created_work_order}`)}
                        sx={{ textTransform: 'none', fontSize: '12px', whiteSpace: 'nowrap' }}
                      >
                        {t('booking.list.open_work_order')}
                      </Button>
                    ) : (
                      <BookingActions
                        booking={booking}
                        t={t}
                        onActionComplete={handleActionComplete}
                      />
                    )}
                  </Box>
                ),
              },
            ]}
            data={items}
            loading={loading}
            onRowClick={handleRowClick}
            sortField={preferences.sortField}
            sortDirection={preferences.sortDirection}
            onSort={handleSort}
            emptyTitle={t('booking.list.no_bookings')}
            pagination={{
              page,
              pageSize,
              totalCount,
              totalPages,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
          />

          {/* Mobile cards */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, flexDirection: 'column', gap: 1.5 }}>
            {items.map((booking) => (
              <Card
                key={booking.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => handleRowClick(booking)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {booking.service_name || '–'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(booking.booking_date)}{' '}
                        {booking.booking_mode === 'drop_off'
                          ? (booking.drop_off_window_name || t('booking.list.drop_off'))
                          : formatTime(booking.booking_time)
                        }
                      </Typography>
                    </Box>
                    <BookingStatusBadge status={booking.status} t={t} />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2">
                        {booking.customer_name}
                        {booking.is_new_customer && (
                          <Chip
                            label={t('booking.list.new_customer')}
                            size="small"
                            color="info"
                            sx={{ ml: 1, height: 18, fontSize: '10px' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.vehicle_registration}
                      </Typography>
                    </Box>
                    <ReminderIcon booking={booking} t={t} />
                  </Box>

                  <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1 }}>
                    {booking.created_work_order && !['pending'].includes(booking.status) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => navigate(`/maintenances/${booking.created_work_order}`)}
                        sx={{ textTransform: 'none', fontSize: '12px' }}
                        fullWidth
                      >
                        {t('booking.list.open_work_order')}
                      </Button>
                    ) : (
                      <BookingActions
                        booking={booking}
                        t={t}
                        onActionComplete={handleActionComplete}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Mobile Pagination */}
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <PaginationControls
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </Box>
        </>
      )}

      {/* Booking Detail Dialog */}
      <BookingDetailDialog
        booking={selectedBooking}
        open={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onActionComplete={(message, severity) => {
          setSnackbar({ open: true, message, severity });
          setSelectedBooking(null);
          refetch();
        }}
        customFormFields={customFormFields}
        t={t}
        navigate={navigate}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(BookingsPage);
